import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CalendarView = ({ isAdmin }) => {
  const { t } = useLanguage();
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [consultations, setConsultations] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  const fetchCalendar = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/consultations/calendar?month=${currentMonth}`);
      if (res.ok) {
        const data = await res.json();
        setConsultations(data.consultations || []);
      }
    } catch { /* logged server-side */ }
  }, [currentMonth]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const year = parseInt(currentMonth.split('-')[0]);
  const month = parseInt(currentMonth.split('-')[1]);
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfWeek = (new Date(year, month - 1, 1).getDay() + 6) % 7; // Monday=0

  const prevMonth = () => {
    const d = new Date(year, month - 2, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    const d = new Date(year, month, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    setSelectedDay(null);
  };

  const getConsultationsForDay = (day) => {
    const dateStr = `${currentMonth}-${String(day).padStart(2, '0')}`;
    return consultations.filter(c => c.date === dateStr);
  };

  const today = new Date().toISOString().split('T')[0];
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const dayNames = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

  const selectedConsultations = selectedDay ? getConsultationsForDay(selectedDay) : [];

  return (
    <div data-testid="calendar-view" className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <button onClick={prevMonth} className="p-2 hover:bg-slate-100 rounded-lg"><ChevronLeft className="w-5 h-5 text-slate-600" /></button>
        <h2 className="text-lg font-bold text-[#0d1442]">{monthNames[month - 1]} {year}</h2>
        <button onClick={nextMonth} className="p-2 hover:bg-slate-100 rounded-lg"><ChevronRight className="w-5 h-5 text-slate-600" /></button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-slate-100">
        {dayNames.map(d => (
          <div key={d} className="py-2 text-center text-xs font-semibold text-slate-500 uppercase">{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {/* Empty cells before first day */}
        {Array.from({ length: firstDayOfWeek }).map((_, i) => (
          <div key={`empty-${i}`} className="h-20 border-b border-r border-slate-50 bg-slate-25" />
        ))}

        {/* Day cells */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dateStr = `${currentMonth}-${String(day).padStart(2, '0')}`;
          const dayConsultations = getConsultationsForDay(day);
          const isToday = dateStr === today;
          const isSelected = selectedDay === day;
          const hasBookings = dayConsultations.length > 0;

          return (
            <div
              key={day}
              onClick={() => setSelectedDay(day === selectedDay ? null : day)}
              className={`h-20 border-b border-r border-slate-50 p-1.5 cursor-pointer transition-colors ${
                isSelected ? 'bg-[#1a237e]/10' : hasBookings ? 'hover:bg-blue-50' : 'hover:bg-slate-50'
              }`}
            >
              <span className={`inline-flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full ${
                isToday ? 'bg-[#1a237e] text-white' : 'text-slate-700'
              }`}>{day}</span>
              {hasBookings && (
                <div className="mt-0.5">
                  {dayConsultations.slice(0, 2).map((c) => (
                    <div key={c.id || c.time_slot} className="text-[10px] leading-tight px-1 py-0.5 mb-0.5 bg-[#1a237e]/10 text-[#1a237e] rounded truncate">
                      {c.time_slot} {isAdmin ? c.name?.split(' ')[0] : ''}
                    </div>
                  ))}
                  {dayConsultations.length > 2 && (
                    <div className="text-[10px] text-slate-500 px-1">+{dayConsultations.length - 2} more</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected day detail */}
      {selectedDay && (
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
          <h3 className="font-semibold text-[#0d1442] mb-3">
            {`${currentMonth}-${String(selectedDay).padStart(2, '0')}`} — {selectedConsultations.length} {selectedConsultations.length === 1 ? 'booking' : 'bookings'}
          </h3>
          {selectedConsultations.length === 0 ? (
            <p className="text-sm text-slate-500">{t('dashboard.noBookings')}</p>
          ) : (
            <div className="space-y-2">
              {selectedConsultations.map(c => (
                <div key={c.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
                  <div className="flex items-center gap-1 text-[#1a237e]">
                    <Clock className="w-4 h-4" />
                    <span className="font-semibold text-sm">{c.time_slot}</span>
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-[#0d1442]">{c.name}</span>
                    {isAdmin && <span className="text-xs text-slate-500 ml-2">{c.company_name} &middot; {c.email}</span>}
                  </div>
                  {c.reminder_sent && <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Reminded</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CalendarView;
